import { useCallback, useEffect, useState } from 'react';
import type {
  AddMasterSensorsResponse,
  AddSensorsResponse,
  CustomerSensor,
  CustomGroupListItem,
  CustomGroupSensor,
  SuggestionGroup,
} from '@metron/shared';
import { Button } from '../ui/button';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { CustomGroupFormFields } from './CustomGroupFormFields';
import { CustomGroupFormActions } from './CustomGroupFormActions';
import { SensorList } from './SensorList';
import { SensorPicker } from './SensorPicker';
import { SuggestionBanner } from './SuggestionBanner';
import { toErrorMessage } from '@metron/shared';

interface UseCustomGroupsHook {
  getSensors: (id: number) => Promise<{ sensors: CustomGroupSensor[] }>;
  addSubmeters: (id: number, data: { sensorIds: number[] }) => Promise<AddSensorsResponse>;
  addMasterSensors: (
    id: number,
    data: { sensorIds: number[] },
  ) => Promise<AddMasterSensorsResponse>;
  removeSubmeter: (id: number, sensorId: number) => Promise<void>;
  removeMasterSensor: (id: number, sensorId: number) => Promise<void>;
  getCustomerSensors: (customerId: number) => Promise<{ sensors: CustomerSensor[] }>;
  getSourceGroupSensors: (sensorGroupId: number) => Promise<{ sensorIds: number[] }>;
}

export interface CustomGroupRowProps {
  group: CustomGroupListItem | null;
  customerId: number;
  isNew: boolean;
  onSave: (data: {
    name: string;
    description?: string;
    address?: string;
    sourceSensorGroupId?: number;
  }) => Promise<unknown>;
  onDelete: (id: number) => Promise<void>;
  onCancel: () => void;
  useCustomGroupsHook: UseCustomGroupsHook;
  suggestions?: SuggestionGroup[];
  hasEditPermission: boolean;
  hasDeletePermission: boolean;
}

interface LocalState {
  name: string;
  description: string;
  address: string;
  isDirty: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  nameError?: string;
}

export function CustomGroupRow({
  group,
  customerId,
  isNew,
  onSave,
  onDelete,
  onCancel,
  useCustomGroupsHook,
  suggestions = [],
  hasEditPermission,
  hasDeletePermission,
}: CustomGroupRowProps) {
  const [localState, setLocalState] = useState<LocalState>({
    name: group?.name || '',
    description: group?.description || '',
    address: group?.address || '',
    isDirty: isNew,
    isSaving: false,
    isDeleting: false,
  });

  const [isExpanded, setIsExpanded] = useState(isNew);
  const [sensors, setSensors] = useState<CustomGroupSensor[]>([]);
  const [isSensorsLoading, setIsSensorsLoading] = useState(false);
  const [customerSensors, setCustomerSensors] = useState<CustomerSensor[]>([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(isNew);
  const [isSuggestionsLoading] = useState(false);
  const [sourceSensorGroupId, setSourceSensorGroupId] = useState<
    number | undefined
  >(undefined);

  // Sync local state from props when group changes (after save)
  useEffect(() => {
    if (group && !localState.isDirty && !localState.isSaving) {
      setLocalState((prev) => ({
        ...prev,
        name: group.name,
        description: group.description || '',
        address: group.address || '',
      }));
    }
  }, [group?.name, group?.description, group?.address, localState.isDirty, localState.isSaving]);

  // Load sensors when expanded for existing groups
  const loadSensors = useCallback(async () => {
    if (!group || isNew) return;
    setIsSensorsLoading(true);
    try {
      const result = await useCustomGroupsHook.getSensors(group.id);
      setSensors(result.sensors);
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to load sensors');
      toast.error(message);
    } finally {
      setIsSensorsLoading(false);
    }
  }, [group, isNew, useCustomGroupsHook]);

  useEffect(() => {
    if (isExpanded && group && !isNew) {
      loadSensors();
    }
  }, [isExpanded, group, isNew, loadSensors]);

  // Load customer sensors for picker
  const loadCustomerSensors = useCallback(async () => {
    try {
      const result = await useCustomGroupsHook.getCustomerSensors(customerId);
      setCustomerSensors(result.sensors);
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to load customer sensors');
      toast.error(message);
    }
  }, [customerId, useCustomGroupsHook]);

  const handleOpenPicker = useCallback(async () => {
    await loadCustomerSensors();
    setIsPickerOpen(true);
  }, [loadCustomerSensors]);

  const handleInputChange = useCallback(
    (field: 'name' | 'description' | 'address', value: string) => {
      setLocalState((prev) => ({
        ...prev,
        [field]: value,
        isDirty: true,
        nameError: field === 'name' && !value.trim() ? 'Group name is required' : prev.nameError,
      }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!localState.name.trim()) {
      setLocalState((prev) => ({ ...prev, nameError: 'Group name is required' }));
      toast.error('Please enter a group name');
      return;
    }

    setLocalState((prev) => ({ ...prev, isSaving: true }));

    try {
      await onSave({
        name: localState.name.trim(),
        description: localState.description.trim() || undefined,
        address: localState.address.trim() || undefined,
        sourceSensorGroupId,
      });

      setLocalState((prev) => ({
        ...prev,
        isDirty: false,
        isSaving: false,
        nameError: undefined,
      }));

      toast.success(isNew ? 'Custom group created' : 'Custom group updated');
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to save custom group');
      toast.error(message);
      setLocalState((prev) => ({ ...prev, isSaving: false }));
    }
  }, [localState.name, localState.description, localState.address, isNew, onSave]);

  const handleCancel = useCallback(() => {
    if (isNew) {
      onCancel();
    } else {
      setLocalState({
        name: group?.name || '',
        description: group?.description || '',
        address: group?.address || '',
        isDirty: false,
        isSaving: false,
        isDeleting: false,
      });
    }
  }, [isNew, group, onCancel]);

  const handleDelete = useCallback(async () => {
    if (!group) return;
    setLocalState((prev) => ({ ...prev, isDeleting: true }));
    try {
      await onDelete(group.id);
      toast.success('Custom group deleted');
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to delete custom group');
      toast.error(message);
      setLocalState((prev) => ({ ...prev, isDeleting: false }));
    }
  }, [group, onDelete]);

  const handleRemoveSubmeter = useCallback(async (sensorId: number) => {
    if (!group) return;
    try {
      await useCustomGroupsHook.removeSubmeter(group.id, sensorId);
      setSensors((prev) => prev.filter((s) => !(s.sensorId === sensorId && s.role === 'submeter')));
      toast.success('Submeter removed');
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to remove submeter');
      toast.error(message);
    }
  }, [group, useCustomGroupsHook]);

  const handleRemoveMaster = useCallback(async (sensorId: number) => {
    if (!group) return;
    try {
      await useCustomGroupsHook.removeMasterSensor(group.id, sensorId);
      setSensors((prev) => prev.filter((s) => !(s.sensorId === sensorId && s.role === 'master')));
      toast.success('Master meter removed');
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to remove master meter');
      toast.error(message);
    }
  }, [group, useCustomGroupsHook]);

  const handleAddSubmeters = useCallback(async (ids: number[]) => {
    if (!group) return;
    try {
      await useCustomGroupsHook.addSubmeters(group.id, { sensorIds: ids });
      await loadSensors();
      toast.success(`Added ${ids.length} submeter(s)`);
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to add submeters');
      toast.error(message);
    }
  }, [group, useCustomGroupsHook, loadSensors]);

  const handleAddMasters = useCallback(async (ids: number[]) => {
    if (!group) return;
    try {
      await useCustomGroupsHook.addMasterSensors(group.id, { sensorIds: ids });
      await loadSensors();
      toast.success(`Added ${ids.length} master meter(s)`);
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to add master meters');
      toast.error(message);
    }
  }, [group, useCustomGroupsHook, loadSensors]);

  const handleSelectSuggestion = useCallback(
    (sensorGroupId: number, name: string) => {
      setLocalState((prev) => ({
        ...prev,
        name,
        isDirty: true,
      }));
      setSourceSensorGroupId(sensorGroupId);
      setShowSuggestions(false);
      toast.info(`Selected "${name}" - sensors will be added after saving`);
    },
    [],
  );

  const existingSensorIds = new Set(sensors.map((s) => s.sensorId));

  const totalSensors = group ? (group.sensorCount + group.masterSensorCount) : sensors.length;

  return (
    <div
      className={`border rounded-lg ${localState.isDirty ? 'border-yellow-500' : ''}`}
    >
      {/* Header / Collapsed View */}
      <div
        className='flex items-center gap-4 p-4 cursor-pointer'
        onClick={() => !isNew && setIsExpanded(!isExpanded)}
      >
        {!isNew && (
          <span className='text-muted-foreground'>
            {isExpanded
              ? <ChevronDown className='h-4 w-4' />
              : <ChevronRight className='h-4 w-4' />}
          </span>
        )}

        <div className='flex-1 min-w-0'>
          {isNew || isExpanded
            ? (
              <div onClick={(e) => e.stopPropagation()}>
                <CustomGroupFormFields
                  name={localState.name}
                  description={localState.description}
                  address={localState.address}
                  onNameChange={(v) => handleInputChange('name', v)}
                  onDescriptionChange={(v) => handleInputChange('description', v)}
                  onAddressChange={(v) => handleInputChange('address', v)}
                  nameError={localState.nameError}
                  disabled={localState.isSaving || !hasEditPermission}
                />
              </div>
            )
            : (
              <div className='flex items-center gap-4'>
                <span className='font-medium truncate'>{group?.name}</span>
                {group?.description && (
                  <span className='text-sm text-muted-foreground truncate hidden md:inline'>
                    {group.description}
                  </span>
                )}
                <span className='text-xs text-muted-foreground shrink-0'>
                  {totalSensors} sensor{totalSensors !== 1 ? 's' : ''}
                </span>
              </div>
            )}
        </div>
      </div>

      {/* Expanded Content */}
      {(isNew || isExpanded) && (
        <div
          className='px-4 pb-4 space-y-4'
          onClick={(e) => e.stopPropagation()}
        >
          {/* Suggestion Banner for new groups */}
          {isNew && showSuggestions && (
            <SuggestionBanner
              suggestions={suggestions}
              onSelectSuggestion={handleSelectSuggestion}
              onDismiss={() => setShowSuggestions(false)}
              isLoading={isSuggestionsLoading}
            />
          )}

          {/* Form Actions */}
          {localState.isDirty && (
            <CustomGroupFormActions
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={handleDelete}
              saveLabel={isNew ? 'Create Group' : 'Save Changes'}
              isSaving={localState.isSaving}
              isDeleting={localState.isDeleting}
              disabled={!hasEditPermission}
              showDelete={!isNew && hasDeletePermission}
              showCancel
            />
          )}

          {/* Sensor Management (only for saved groups) */}
          {!isNew && group && (
            <>
              <SensorList
                sensors={sensors}
                onRemoveSubmeter={handleRemoveSubmeter}
                onRemoveMaster={handleRemoveMaster}
                isLoading={isSensorsLoading}
                hasEditPermission={hasEditPermission}
              />

              {hasEditPermission && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleOpenPicker}
                >
                  <Plus className='h-4 w-4 mr-2' />
                  Add Sensors
                </Button>
              )}

              <SensorPicker
                open={isPickerOpen}
                onOpenChange={setIsPickerOpen}
                sensors={customerSensors}
                existingSensorIds={existingSensorIds}
                onAddSubmeters={handleAddSubmeters}
                onAddMasters={handleAddMasters}
              />
            </>
          )}

          {/* Delete for non-dirty existing groups */}
          {!isNew && !localState.isDirty && hasDeletePermission && (
            <div className='flex justify-end'>
              <CustomGroupFormActions
                onSave={() => {}}
                onDelete={handleDelete}
                isDeleting={localState.isDeleting}
                disabled={!hasDeletePermission}
                showDelete
                showCancel={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
